import React, { useState, useEffect, useCallback } from "react";
import ReportExportView from "./ReportExportView";
import { exportAttendanceRange, loadAttendancePeople } from "../../utils/reportData";
import { monthKey } from "../../utils/reportExports";
import {
  collection,
  query,
  onSnapshot,
  deleteDoc,
  doc,
  addDoc,
  getDoc,
  serverTimestamp,
  orderBy,
  where,
  getDocs,
  Timestamp
} from "../../config/dataClient";
import {
  Calculator,
  Trash2,
  Plus,
  AlertTriangle,
  FileSpreadsheet,
  Calendar as CalIcon,
  User,
  Clock,
  ChevronDown,
  ChevronUp,
  ChevronLeft,
  ChevronRight,
  Briefcase
} from "lucide-react";
import { db } from "../../config/firebase";
import { APP_ID, ROLES } from "../../config/constants";
import {
  formatDate,
  formatTime,
  getDaysInMonth,
} from "../../utils/timeHelpers";
import { useToast, useConfirm } from "../ui/ToastConfirm";

import { escapeHtml, processLogsWithDuplicates, calculatePeriodStats } from "../../utils/attendanceStats";

export default function HoursView({ user, isAdmin, role }) {
  const [allUsers, setAllUsers] = useState([]);
  const [logsCache, setLogsCache] = useState({});
  const [leavesCache, setLeavesCache] = useState({});
  const [expandedUid, setExpandedUid] = useState(null);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [showExportModal, setShowExportModal] = useState(false);
  const [showDetailExport, setShowDetailExport] = useState(false);
  const loadReportUsers = useCallback(() => loadAttendancePeople(isAdmin ? ROLES.ADMIN : null), [isAdmin]);
  const [exportSelectedUids, setExportSelectedUids] = useState(new Set());
  const [exportStartMonth, setExportStartMonth] = useState("");
  const [exportEndMonth, setExportEndMonth] = useState("");
  const [showPunchModal, setShowPunchModal] = useState(false);
  const [punchData, setPunchData] = useState({
    uid: "",
    date: "",
    time: "",
    type: "in",
    reason: "", 
  });
  
  const [localPermission, setLocalPermission] = useState(false);
  const showToast = useToast();
  const confirm = useConfirm();

  useEffect(() => {
    if (!user) return;
    getDoc(
      doc(db, "artifacts", APP_ID, "users", user.uid, "profile", "main")
    ).then((s) => {
      if (s.exists()) {
          const d = s.data();
          setLocalPermission(d.canViewAllHours || false);
      }
    });
  }, [user]);

  const canManage = isAdmin || localPermission === true;

  // 讀取員工列表
  // [優化] 優先讀取 daily_status 中的 fullName 欄位（已冗餘儲存），避免 N+1
  useEffect(() => {
    const qUsers = query(
      collection(db, "artifacts", APP_ID, "public", "data", "daily_status")
    );
    const unsub = onSnapshot(qUsers, async (snapshot) => {
      const promises = snapshot.docs.map(async (d) => {
        const data = d.data();
        if (data.isHidden) return null;

        let displayName = data.fullName || data.name;

        // [向下相容] 舊資料 daily_status 沒 fullName，再去 profile 抓
        if (!data.fullName) {
          try {
            const pSnap = await getDoc(doc(db, "artifacts", APP_ID, "users", d.id, "profile", "main"));
            if (pSnap.exists() && pSnap.data().fullName) {
              displayName = pSnap.data().fullName;
            }
          } catch (e) { /* 權限不足時略過 */ }
        }

        return {
          uid: d.id,
          ...data,
          name: displayName,
          photoURL: data.photoURL,
          role: data.role || ROLES.PARTTIME,
          accountStatus: data.accountStatus || "active",
        };
      });
      const users = (await Promise.all(promises)).filter(Boolean);
      setAllUsers(users);
    });
    return () => unsub();
  }, []);

  // 讀取打卡與請假紀錄
  useEffect(() => {
    if (allUsers.length === 0) return;
    const unsubs = [];
    allUsers.forEach((u) => {
      const qLogs = query(
        collection(db, "artifacts", APP_ID, "users", u.uid, "logs")
      );
      unsubs.push(
        onSnapshot(qLogs, (snapshot) => {
          const userLogs = snapshot.docs
            .map((d) => ({
              id: d.id,
              ...d.data(),
              dateObj: d.data().timestamp?.toDate(),
            }))
            .filter((l) => l.dateObj);
          const processedLogs = processLogsWithDuplicates(userLogs);
          setLogsCache((prev) => ({ ...prev, [u.uid]: processedLogs }));
        })
      );
    });

    if (role === ROLES.FULLTIME || canManage) {
        const qLeaves = query(
          collection(db, "artifacts", APP_ID, "public", "data", "leaves")
        );
        unsubs.push(
          onSnapshot(qLeaves, (snapshot) => {
            const leavesMap = {};
            snapshot.docs.forEach((doc) => {
              const data = doc.data();
              if (!leavesMap[data.uid]) leavesMap[data.uid] = [];
              leavesMap[data.uid].push({ ...data, id: doc.id });
            });
            setLeavesCache(leavesMap);
          }, (error) => {
              console.warn("Leaves fetch blocked:", error);
          })
        );
    }

    return () => unsubs.forEach((fn) => fn());
  }, [allUsers, role, canManage]);

  const handleDelete = async (uid, logId, dateStr) => {
    const ok = await confirm({
      title: "刪除打卡紀錄",
      message: `確定刪除 ${dateStr} 的這筆打卡？此操作無法復原。`,
      confirmText: "刪除",
      danger: true,
    });
    if (!ok) return;
    try {
      await deleteDoc(
        doc(db, "artifacts", APP_ID, "users", uid, "logs", logId)
      );

      const operator = allUsers.find(u => u.uid === user.uid);
      const operatorName = operator ? operator.name : "管理員";
      const targetUser = allUsers.find(u => u.uid === uid);
      const targetName = targetUser ? targetUser.name : "未知用戶";

      await addDoc(collection(db, "artifacts", APP_ID, "public", "data", "audit_logs"), {
          actionType: "刪除打卡",
          description: `${operatorName} 刪除了 ${targetName} 在 ${dateStr} 的打卡紀錄`,
          operatorName: operatorName,
          operatorUid: user.uid,
          timestamp: serverTimestamp()
      });

    } catch (e) {
      alert("刪除失敗");
    }
  };

  const openAddModal = (uid, dateStr) => {
    const userLogs = logsCache[uid] || [];
    const dayLogs = userLogs.filter(
      (l) => formatDate(l.dateObj) === dateStr && !l.isIgnored
    );
    const nextType = dayLogs.length % 2 === 0 ? "in" : "out";
    const defaultTime = nextType === "in" ? "09:00" : "18:00";
    setPunchData({ uid, date: dateStr, time: defaultTime, type: nextType, reason: "" });
    setShowPunchModal(true);
  };

  const submitAddLog = async () => {
    try {
      if(!punchData.reason) return alert("請輸入補打卡原因");

      const d = new Date(`${punchData.date}T${punchData.time}`);
      await addDoc(
        collection(db, "artifacts", APP_ID, "users", punchData.uid, "logs"),
        {
          uid: punchData.uid,
          timestamp: Timestamp.fromDate(d),
          type: punchData.type,
          device: "manual",
          ip: "manual",
          reason: punchData.reason, 
        }
      );

      const operator = allUsers.find(u => u.uid === user.uid);
      const operatorName = operator ? operator.name : "管理員";
      const targetUser = allUsers.find(u => u.uid === punchData.uid);
      const targetName = targetUser ? targetUser.name : "未知用戶";
      const typeStr = punchData.type === 'in' ? '上班' : '下班';

      await addDoc(collection(db, "artifacts", APP_ID, "public", "data", "audit_logs"), {
          actionType: "補打卡",
          description: `${operatorName} 幫 ${targetName} 補打卡：${punchData.date} ${punchData.time} (${typeStr})，原因：${punchData.reason}`,
          operatorName: operatorName,
          operatorUid: user.uid,
          timestamp: serverTimestamp()
      });

      setShowPunchModal(false);
    } catch (e) {
      alert("新增失敗");
    }
  };

  // --- 匯出功能 ---

  const openExportModal = () => {
    if (!isAdmin) return;
    const monthStr = `${currentMonth.getFullYear()}-${String(currentMonth.getMonth() + 1).padStart(2, "0")}`;
    setExportStartMonth(monthStr);
    setExportEndMonth(monthStr);
    setExportSelectedUids(new Set(allUsers.map((u) => u.uid)));
    setShowExportModal(true);
  };

  const handleExportCalendarRange = () => {
    if (!isAdmin) return;
    if (!exportStartMonth || !exportEndMonth) return alert("請選擇月份範圍");
    if (exportSelectedUids.size === 0) return alert("請選擇至少一位員工");

    const [sy, sm] = exportStartMonth.split("-").map(Number);
    const [ey, em] = exportEndMonth.split("-").map(Number);
    if (sy * 12 + sm > ey * 12 + em) return alert("開始月份不能晚於結束月份");

    const months = [];
    let cy = sy, cm = sm;
    while (cy * 12 + cm <= ey * 12 + em) {
      months.push({ year: cy, month: cm - 1 });
      cm++;
      if (cm > 12) { cm = 1; cy++; }
    }

    const selectedUsers = allUsers.filter((u) => exportSelectedUids.has(u.uid));

    const commonCSS = `
      @import url('https://fonts.googleapis.com/css2?family=Noto+Sans+TC:wght@400;700;900&display=swap');
      body { font-family: 'Noto Sans TC', sans-serif; background-color: #f8fafc; padding: 40px; color: #334155; }
      h1 { text-align: center; color: #1e293b; margin-bottom: 40px; font-size: 28px; }
      h2 { color: #1e293b; font-size: 22px; font-weight: 900; margin: 50px 0 20px; padding: 12px 20px; background: #1e293b; color: white; border-radius: 12px; page-break-before: always; }
      .summary-container { display: flex; gap: 20px; margin-bottom: 40px; flex-wrap: wrap; }
      .summary-section { flex: 1; min-width: 300px; background: white; border-radius: 16px; padding: 24px; border: 1px solid #e2e8f0; box-shadow: 0 4px 6px -1px rgb(0 0 0 / 0.1); }
      .summary-header { font-size: 20px; font-weight: 800; margin-bottom: 20px; padding-bottom: 10px; border-bottom: 3px solid #f1f5f9; color: #1e293b; }
      .summary-row { padding: 12px 0; border-bottom: 1px solid #f1f5f9; display: flex; justify-content: space-between; align-items: center; }
      .summary-name { font-weight: bold; font-size: 16px; color: #334155; min-width: 80px; }
      .stats-group { display: flex; gap: 10px; flex-wrap: wrap; justify-content: flex-end; }
      .stat-badge { display: flex; flex-direction: column; align-items: center; background: #f8fafc; padding: 6px 12px; border-radius: 8px; border: 1px solid #e2e8f0; min-width: 60px; }
      .stat-label { font-size: 11px; color: #64748b; font-weight: bold; margin-bottom: 2px; }
      .stat-val { font-size: 18px; font-weight: 900; color: #0f172a; line-height: 1; }
      .stat-val.hours { color: #2563eb; } .stat-val.late { color: #dc2626; } .stat-val.leave { color: #d97706; }
      .group-title { font-size: 18px; font-weight: bold; margin-top: 30px; margin-bottom: 15px; color: #334155; border-left: 6px solid #2563eb; padding-left: 15px; }
      .user-section { background: white; border-radius: 20px; box-shadow: 0 10px 15px -3px rgb(0 0 0 / 0.1); margin-bottom: 30px; overflow: hidden; page-break-inside: avoid; border: 1px solid #e2e8f0; }
      .user-header { background: #f1f5f9; padding: 15px 20px; border-bottom: 1px solid #e2e8f0; display: flex; justify-content: space-between; align-items: center; }
      .user-title { font-size: 18px; font-weight: bold; color: #0f172a; }
      .user-stats { font-size: 14px; color: #64748b; }
      .calendar { display: grid; grid-template-columns: repeat(7, 1fr); background: #cbd5e1; gap: 1px; border: 1px solid #cbd5e1; }
      .day-header { background: #f8fafc; padding: 8px; text-align: center; font-weight: bold; color: #64748b; font-size: 12px; }
      .day-cell { background: white; min-height: 80px; padding: 6px; position: relative; }
      .day-cell.weekend { background: #fcfcfc; }
      .date-num { font-size: 12px; font-weight: bold; color: #94a3b8; margin-bottom: 4px; display: block; }
      .tag { font-size: 10px; padding: 2px 6px; border-radius: 4px; margin-bottom: 2px; font-weight: bold; display: block; }
      .tag-work { background: #dbeafe; color: #1e40af; border-left: 3px solid #2563eb; }
      .tag-leave { background: #ffedd5; color: #9a3412; border-left: 3px solid #f97316; }
      .tag-error { background: #fee2e2; color: #991b1b; border-left: 3px solid #ef4444; }
    `;

    const names = selectedUsers.map((u) => u.name).join("、");
    const title = months.length === 1
      ? `${sy}年${sm}月 出勤記錄`
      : `${sy}年${sm}月 ～ ${ey}年${em}月 出勤記錄`;

    const safeTitle = escapeHtml(title);
    let html = `<html><head><meta charset="utf-8"><title>${safeTitle}</title><style>${commonCSS}</style></head><body>`;
    html += `<h1>📅 ${safeTitle}</h1><p style="text-align:center;color:#64748b;margin-bottom:40px">員工：${escapeHtml(names)}</p>`;

    months.forEach(({ year, month }) => {
      const days = getDaysInMonth(year, month);
      html += `<h2>📆 ${year}年${month + 1}月</h2>`;

      // 摘要區
      const ftUsers = selectedUsers.filter((u) => u.role === ROLES.FULLTIME);
      const ptUsers = selectedUsers.filter((u) => u.role === ROLES.PARTTIME);
      html += `<div class="summary-container">`;

      if (ftUsers.length > 0) {
        html += `<div class="summary-section"><div class="summary-header">正職人員總覽</div>`;
        ftUsers.forEach((u) => {
          const uLogs = logsCache[u.uid] || [];
          const uLeaves = leavesCache[u.uid] || [];
          const { totalLateMinutes, dailyStats, leaveSummary } = calculatePeriodStats(u.uid, uLogs, uLeaves, u.role, year, month);
          if (u.accountStatus === "archived" && Object.keys(dailyStats).length === 0) return;
          let totalLeaveHours = 0;
          Object.values(leaveSummary).forEach((v) => (totalLeaveHours += v.hours));
          const leaveText = Object.entries(leaveSummary).map(([type, val]) => {
            const isWholeDay = val.days % 1 === 0;
            return isWholeDay ? `${type}${val.days}天` : `${type}${val.days}天(${val.hours}hr)`;
          }).join(" ");
          html += `<div class="summary-row"><span class="summary-name">${escapeHtml(u.name)}</span><div class="stats-group">
            ${totalLateMinutes > 0 ? `<div class="stat-badge"><span class="stat-label">遲到</span><span class="stat-val late">${totalLateMinutes}分鐘</span></div>` : ""}
            ${totalLeaveHours > 0 ? `<div class="stat-badge" style="min-width:auto"><span class="stat-label">請假統計</span><span class="stat-val leave" style="font-size:12px">${escapeHtml(leaveText)}</span></div>` : ""}
            ${totalLateMinutes === 0 && totalLeaveHours === 0 ? `<div class="stat-badge"><span class="stat-label">狀態</span><span class="stat-val" style="color:green">全勤</span></div>` : ""}
          </div></div>`;
        });
        html += `</div>`;
      }

      if (ptUsers.length > 0) {
        html += `<div class="summary-section"><div class="summary-header">工讀人員總覽</div>`;
        ptUsers.forEach((u) => {
          const uLogs = logsCache[u.uid] || [];
          const uLeaves = leavesCache[u.uid] || [];
          const { totalHours, dailyStats } = calculatePeriodStats(u.uid, uLogs, uLeaves, u.role, year, month);
          if (u.accountStatus === "archived" && Object.keys(dailyStats).length === 0) return;
          html += `<div class="summary-row"><span class="summary-name">${escapeHtml(u.name)}</span><div class="stats-group">
            <div class="stat-badge"><span class="stat-label">總工時</span><span class="stat-val hours">${totalHours}</span></div>
          </div></div>`;
        });
        html += `</div>`;
      }
      html += `</div>`;

      // 月曆明細
      const groups = { [ROLES.FULLTIME]: "正職人員明細", [ROLES.PARTTIME]: "工讀人員明細" };
      Object.keys(groups).forEach((roleKey) => {
        const groupUsers = selectedUsers.filter((u) => u.role === roleKey);
        if (groupUsers.length === 0) return;
        html += `<div class="group-title">${groups[roleKey]}</div>`;
        groupUsers.forEach((u) => {
          const uLogs = logsCache[u.uid] || [];
          const uLeaves = leavesCache[u.uid] || [];
          const { totalHours, totalLateMinutes, dailyStats, leaveSummary } = calculatePeriodStats(u.uid, uLogs, uLeaves, u.role, year, month);
          if (u.accountStatus === "archived" && Object.keys(dailyStats).length === 0) return;

          let statsStr = "";
          if (roleKey === ROLES.FULLTIME) {
            statsStr += `遲到: ${totalLateMinutes}分鐘`;
            const lStr = Object.entries(leaveSummary).map(([k, v]) => {
              const isWholeDay = v.days % 1 === 0;
              return isWholeDay ? `${k}${v.days}天` : `${k}${v.days}天(${v.hours}h)`;
            }).join(" ");
            if (lStr) statsStr += ` | ${lStr}`;
          } else {
            statsStr = `工時: ${totalHours}hr`;
          }

          html += `<div class="user-section"><div class="user-header"><div class="user-title">${escapeHtml(u.name)}</div><div class="user-stats">${escapeHtml(statsStr)}</div></div><div class="calendar">`;
          ["日","一","二","三","四","五","六"].forEach((d) => (html += `<div class="day-header">${d}</div>`));
          const startDay = new Date(year, month, 1).getDay();
          for (let i = 0; i < startDay; i++) html += `<div class="day-cell"></div>`;
          days.forEach((day) => {
            const dateStr = formatDate(day);
            const isWeekend = day.getDay() === 0 || day.getDay() === 6;
            html += `<div class="day-cell ${isWeekend ? "weekend" : ""}"><span class="date-num">${day.getDate()}</span>`;
            const stats = dailyStats[dateStr];
            if (stats) {
              if (stats.isLate) {
                html += `<div class="tag tag-error">遲到${stats.lateMinutes}分</div><div style="font-size:10px">${stats.rawHtml}</div>`;
              } else if (stats.isAbnormal) {
                html += `<div class="tag tag-error">缺卡</div><div style="font-size:10px">${stats.rawHtml}</div>`;
              } else {
                html += `<div class="tag tag-work">${stats.ranges}</div>`;
              }
            }
            const leaves = uLeaves.filter((l) => {
              if (l.status !== "approved") return false;
              const s = new Date(l.startDate); s.setHours(0,0,0,0);
              const e = new Date(l.endDate); e.setHours(23,59,59,999);
              return day >= s && day <= e;
            });
            leaves.forEach((l) => { html += `<div class="tag tag-leave">${escapeHtml(l.typeName)}</div>`; });
            html += `</div>`;
          });
          html += `</div></div>`;
        });
      });
    });

    html += `</body></html>`;
    const blob = new Blob([html], { type: "text/html;charset=utf-8" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    const fname = months.length === 1
      ? `出勤月曆_${sy}年${sm}月.html`
      : `出勤月曆_${sy}年${sm}月_至_${ey}年${em}月.html`;
    link.download = fname;
    link.click();
    setShowExportModal(false);
  };

  // --- 分組邏輯 ---
  // 先篩選出管理員可見的使用者
  const visibleUsers = allUsers.filter(u => canManage || u.uid === user.uid);
  
  // 分組：上班中 vs 非上班中 (包含下班、未打卡、離職)
  const workingUsers = visibleUsers.filter(u => u.status === 'in');
  const offDutyUsers = visibleUsers.filter(u => u.status !== 'in');

  // 渲染單個使用者卡片的函式 (避免重複代碼)
  const renderUserCard = (u) => {
    const uLogs = logsCache[u.uid] || [];
    const uLeaves = leavesCache[u.uid] || [];
    const { totalHours, dailyStats } = calculatePeriodStats(
        u.uid,
        uLogs,
        uLeaves,
        u.role,
        currentMonth.getFullYear(),
        currentMonth.getMonth()
    );

    if (u.accountStatus === "archived" && Object.keys(dailyStats).length === 0) return null;

    const hasError = Object.values(dailyStats).some((s) => s.isAbnormal);
    const isExpanded = expandedUid === u.uid;
    const isIn = u.status === "in";

    return (
        <div
            key={u.uid}
            className={`paper-item employee-hours bg-white rounded-xl shadow-sm border transition-all duration-200 mb-3 ${
                hasError ? "border-red-200" : (isIn ? "border-green-300 ring-2 ring-green-100" : "border-slate-100")
            } ${u.accountStatus === "archived" ? "opacity-70 bg-slate-50" : ""}`}
        >
            <div
                className="p-4 flex justify-between items-center cursor-pointer hover:bg-slate-50"
                onClick={() => setExpandedUid(isExpanded ? null : u.uid)}
            >
                <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-full bg-slate-100 overflow-hidden border border-slate-200 flex-shrink-0 relative">
                        {u.photoURL ? (
                            <img
                                src={u.photoURL}
                                className="w-full h-full object-cover"
                                alt={u.name}
                                referrerPolicy="no-referrer"
                            />
                        ) : (
                            <User className="p-2 w-full h-full text-slate-400" />
                        )}
                        {isIn && <div className="absolute bottom-0 right-0 w-3 h-3 bg-green-500 rounded-full border-2 border-white"></div>}
                    </div>
                    <div className="flex flex-col">
                        <div className="flex items-center gap-2">
                            <div className="font-bold text-slate-700 text-lg">
                                {u.name}
                            </div>
                            {isIn ? (
                                <span className="bg-green-100 text-green-700 text-[10px] px-2 py-0.5 rounded-full font-bold">
                                    工作中
                                </span>
                            ) : (
                                <span className="bg-slate-100 text-slate-400 text-[10px] px-2 py-0.5 rounded-full">
                                    下班
                                </span>
                            )}
                            {u.accountStatus === "archived" && (
                                <span className="text-xs text-white bg-slate-400 px-2 py-0.5 rounded">
                                    已離職
                                </span>
                            )}
                        </div>
                        {u.lastTime && (
                            <div className="text-xs text-slate-400">
                                {isIn ? "上班時間: " : "最後打卡: "}
                                {formatTime(u.lastTime.toDate())}
                            </div>
                        )}
                        {hasError && (
                            <div className="flex items-center gap-1 text-red-500 text-xs mt-1">
                                <AlertTriangle size={12} />
                                <span>有缺卡異常</span>
                            </div>
                        )}
                    </div>
                </div>
                <div className="text-right flex items-center gap-3">
                    <div className="text-3xl font-bold text-blue-600 font-mono tracking-tight">
                        {totalHours}
                    </div>
                    {isExpanded ? <ChevronUp size={20} className="text-slate-400"/> : <ChevronDown size={20} className="text-slate-400"/>}
                </div>
            </div>

            {isExpanded && (
                <div className="bg-slate-50 border-t border-slate-100 p-3 space-y-3">
                    {Object.keys(dailyStats)
                        .sort()
                        .map((dateStr) => {
                            const stats = dailyStats[dateStr];
                            return (
                                <div
                                    key={dateStr}
                                    className="bg-white border border-slate-200 rounded-lg p-3 shadow-sm"
                                >
                                    <div className="flex justify-between items-center mb-2 pb-2 border-b border-slate-50">
                                        <div className="flex items-center gap-2">
                                            <span className="font-bold text-slate-700 text-sm">
                                                {dateStr}
                                            </span>
                                            {stats.isLate && (
                                                <span className="bg-red-100 text-red-600 text-[10px] px-2 py-0.5 rounded-full flex items-center font-bold">
                                                    遲到{stats.lateMinutes}分鐘
                                                </span>
                                            )}
                                            {stats.isAbnormal && (
                                                <span className="bg-red-100 text-red-600 text-[10px] px-2 py-0.5 rounded-full flex items-center font-bold">
                                                    缺卡
                                                </span>
                                            )}
                                        </div>
                                        {canManage && (
                                            <Plus
                                                size={18}
                                                className="text-green-600 cursor-pointer hover:scale-110"
                                                onClick={() => openAddModal(u.uid, dateStr)}
                                            />
                                        )}
                                    </div>
                                    <div className="text-xs text-slate-500 mb-2 space-y-1">
                                        <div className="flex justify-between">
                                            <span>原始:</span>{" "}
                                            <span
                                                className="font-mono text-slate-700"
                                                dangerouslySetInnerHTML={{
                                                    __html: stats.rawHtml,
                                                }}
                                            ></span>
                                        </div>
                                        <div className="flex justify-between">
                                            <span>區間:</span>{" "}
                                            <span className="font-mono text-blue-600 font-bold">
                                                {stats.ranges || "無"}
                                            </span>
                                        </div>
                                        <div className="flex justify-between">
                                            <span>工時:</span>{" "}
                                            <span className="font-mono text-slate-700 font-bold">
                                                {stats.hours} hr
                                            </span>
                                        </div>
                                    </div>
                                    <div className="flex flex-wrap gap-2 mt-2 pt-2 border-t border-slate-50">
                                        {stats.logs &&
                                            stats.logs.map((log) => (
                                                <div
                                                    key={log.id}
                                                    className={`flex items-center gap-1 px-2 py-1 rounded border text-xs ${
                                                        log.device === "manual"
                                                            ? "bg-purple-100 text-purple-700 border-purple-200"
                                                            : log.type === "in"
                                                                ? "bg-green-50 text-green-700"
                                                                : "bg-orange-50 text-orange-700"
                                                    } ${
                                                        log.isIgnored
                                                            ? "opacity-50 line-through"
                                                            : ""
                                                    }`}
                                                >
                                                    <span className="font-mono font-bold">
                                                        {formatTime(log.dateObj)}
                                                    </span>
                                                    {log.reason && (
                                                        <span className="text-[10px] ml-1 opacity-75 border-l border-current pl-1">
                                                            {log.reason}
                                                        </span>
                                                    )}

                                                    {canManage && (
                                                        <Trash2
                                                            size={12}
                                                            className="cursor-pointer hover:text-red-600 ml-1"
                                                            onClick={() =>
                                                                handleDelete(u.uid, log.id, dateStr)
                                                            }
                                                        />
                                                    )}
                                                </div>
                                            ))}
                                    </div>
                                </div>
                            );
                        })}
                </div>
            )}
        </div>
    );
  };

  if (showDetailExport && isAdmin) return <ReportExportView
    key={user.uid}
    title="匯出出勤明細"
    initialMonth={monthKey(currentMonth)}
    loadUsers={loadReportUsers}
    onBack={() => setShowDetailExport(false)}
    onExport={options => exportAttendanceRange(isAdmin ? ROLES.ADMIN : null, options)}
  />;

  return (
    <div className="paper-page hours-view p-4 pb-20">
      <div className="page-actions flex justify-between items-center mb-4 gap-2 overflow-x-auto scrollbar-hide">
        <h2 className="text-lg font-bold text-slate-800 flex items-center gap-2 whitespace-nowrap">
          <Calculator className="w-5 h-5 text-blue-600" /> 工時管理
        </h2>
        {isAdmin && (
          <div className="flex gap-2">
            <button
              onClick={() => { if (isAdmin) setShowDetailExport(true); }}
              className="bg-white border border-slate-200 text-slate-700 px-3 py-2 rounded-lg shadow-sm text-sm font-bold flex items-center gap-2 hover:bg-slate-50 transition-colors whitespace-nowrap"
            >
              <FileSpreadsheet size={16} /> 匯出明細
            </button>
            <button
              onClick={openExportModal}
              className="bg-white border border-slate-200 text-slate-700 px-3 py-2 rounded-lg shadow-sm text-sm font-bold flex items-center gap-2 hover:bg-slate-50 transition-colors whitespace-nowrap"
            >
              <CalIcon size={16} /> 匯出月曆
            </button>
          </div>
        )}
      </div>

      <div className="paper-month-nav bg-white p-3 rounded-xl border border-slate-100 mb-4 flex justify-between items-center shadow-sm">
        <button
          onClick={() =>
            setCurrentMonth(
              new Date(currentMonth.setMonth(currentMonth.getMonth() - 1))
            )
          }
          aria-label="上個月" title="上個月" className="lake-icon-button p-2 hover:bg-slate-50 rounded"
        >
          <ChevronLeft size={20} />
        </button>
        <div className="font-bold text-slate-700">
          {currentMonth.getFullYear()}年 {currentMonth.getMonth() + 1}月
        </div>
        <button
          onClick={() =>
            setCurrentMonth(
              new Date(currentMonth.setMonth(currentMonth.getMonth() + 1))
            )
          }
          aria-label="下個月" title="下個月" className="lake-icon-button p-2 hover:bg-slate-50 rounded"
        >
          <ChevronRight size={20} />
        </button>
      </div>

      {/* 分區顯示列表 */}
      <div className="space-y-6">
          {/* 工作中區塊 */}
          {workingUsers.length > 0 && (
              <div>
                  <h3 className="text-sm font-bold text-green-600 mb-2 flex items-center gap-2 px-1">
                      <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse"></div>
                      工作中 ({workingUsers.length})
                  </h3>
                  <div>
                      {workingUsers.map(u => renderUserCard(u))}
                  </div>
              </div>
          )}

          {/* 非工作中區塊 */}
          {offDutyUsers.length > 0 && (
              <div>
                  <h3 className="text-sm font-bold text-slate-500 mb-2 flex items-center gap-2 px-1">
                      <div className="w-2 h-2 rounded-full bg-slate-300"></div>
                      非工作中 ({offDutyUsers.length})
                  </h3>
                  <div>
                      {offDutyUsers.map(u => renderUserCard(u))}
                  </div>
              </div>
          )}
          
          {visibleUsers.length === 0 && (
              <div className="text-center text-slate-400 py-10">
                  暫無人員資料
              </div>
          )}
      </div>

      {showPunchModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4 animate-fade-in">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6">
            <h3 className="font-bold text-xl mb-4 text-slate-800">
              補打卡 ({punchData.date})
            </h3>
            <div className="space-y-4">
              <div>
                <label className="block text-xs font-bold text-slate-500 mb-1">
                  時間
                </label>
                <input
                  type="time"
                  className="w-full border rounded-lg p-3 text-lg bg-slate-50 outline-none focus:ring-2 focus:ring-blue-500"
                  value={punchData.time}
                  onChange={(e) =>
                    setPunchData({ ...punchData, time: e.target.value })
                  }
                />
              </div>
              
              <div>
                <label className="block text-xs font-bold text-slate-500 mb-1">
                  類型
                </label>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setPunchData({ ...punchData, type: "in" })}
                    className={`flex-1 py-3 rounded-lg font-bold transition-all ${
                      punchData.type === "in"
                        ? "bg-green-600 text-white shadow-md"
                        : "bg-slate-100 text-slate-500 hover:bg-slate-200"
                    }`}
                  >
                    上班
                  </button>
                  <button
                    type="button"
                    onClick={() => setPunchData({ ...punchData, type: "out" })}
                    className={`flex-1 py-3 rounded-lg font-bold transition-all ${
                      punchData.type === "out"
                        ? "bg-orange-500 text-white shadow-md"
                        : "bg-slate-100 text-slate-500 hover:bg-slate-200"
                    }`}
                  >
                    下班
                  </button>
                </div>
              </div>
              
              <div>
                <label className="block text-xs font-bold text-slate-500 mb-1">
                  補打卡原因
                </label>
                <input
                  type="text"
                  className="w-full border rounded-lg p-3 text-sm bg-slate-50 outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="請輸入原因 (例如: 忘記帶卡)"
                  value={punchData.reason}
                  onChange={(e) =>
                    setPunchData({ ...punchData, reason: e.target.value })
                  }
                />
              </div>

              <div className="flex gap-2 pt-2">
                <button
                  onClick={() => setShowPunchModal(false)}
                  className="flex-1 bg-slate-100 text-slate-600 py-3 rounded-lg font-bold hover:bg-slate-200"
                >
                  取消
                </button>
                <button
                  onClick={submitAddLog}
                  className="flex-1 bg-blue-600 text-white py-3 rounded-lg font-bold hover:bg-blue-700 shadow-md"
                >
                  確認新增
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showExportModal && isAdmin && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4 animate-fade-in">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6 max-h-[90vh] overflow-y-auto">
            <h3 className="font-bold text-xl mb-5 text-slate-800 flex items-center gap-2">
              <CalIcon size={20} className="text-blue-600" /> 匯出月曆設定
            </h3>
            <div className="space-y-5">
              {/* 月份範圍 */}
              <div>
                <label className="block text-xs font-bold text-slate-500 mb-2">月份範圍</label>
                <div className="flex items-center gap-2">
                  <input type="month" className="flex-1 border rounded-lg p-2.5 text-sm bg-slate-50 outline-none focus:ring-2 focus:ring-blue-500" value={exportStartMonth} onChange={(e) => setExportStartMonth(e.target.value)} />
                  <span className="text-slate-400 font-bold">～</span>
                  <input type="month" className="flex-1 border rounded-lg p-2.5 text-sm bg-slate-50 outline-none focus:ring-2 focus:ring-blue-500" value={exportEndMonth} onChange={(e) => setExportEndMonth(e.target.value)} />
                </div>
              </div>

              {/* 員工選擇 */}
              <div>
                <div className="flex justify-between items-center mb-2">
                  <label className="text-xs font-bold text-slate-500">選擇員工</label>
                  <div className="flex gap-2">
                    <button type="button" onClick={() => setExportSelectedUids(new Set(allUsers.map((u) => u.uid)))} className="text-xs text-blue-600 hover:underline">全選</button>
                    <button type="button" onClick={() => setExportSelectedUids(new Set())} className="text-xs text-slate-400 hover:underline">清除</button>
                  </div>
                </div>
                <div className="border rounded-lg bg-slate-50 max-h-48 overflow-y-auto divide-y divide-slate-100">
                  {allUsers.map((u) => (
                    <label key={u.uid} className="flex items-center gap-3 px-3 py-2.5 cursor-pointer hover:bg-blue-50 transition-colors">
                      <input
                        type="checkbox"
                        checked={exportSelectedUids.has(u.uid)}
                        onChange={(e) => {
                          const next = new Set(exportSelectedUids);
                          e.target.checked ? next.add(u.uid) : next.delete(u.uid);
                          setExportSelectedUids(next);
                        }}
                        className="w-4 h-4 rounded text-blue-600"
                      />
                      <span className="text-sm text-slate-700">{u.name}</span>
                      <span className="text-xs text-slate-400 ml-auto">{u.role === ROLES.FULLTIME ? "正職" : "工讀"}</span>
                    </label>
                  ))}
                </div>
                <p className="text-xs text-slate-400 mt-1">已選 {exportSelectedUids.size} / {allUsers.length} 人</p>
              </div>

              {/* 按鈕 */}
              <div className="flex gap-2 pt-2">
                <button onClick={() => setShowExportModal(false)} className="flex-1 bg-slate-100 text-slate-600 py-3 rounded-lg font-bold hover:bg-slate-200">取消</button>
                <button onClick={handleExportCalendarRange} className="flex-1 bg-blue-600 text-white py-3 rounded-lg font-bold hover:bg-blue-700 shadow-md">匯出</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
